import { useCallback } from "react";

import {
  DdLogs,
  DdRum,
  ErrorSource,
  initDatadog,
  setDatadogUser,
} from "@/crossPlatform/Datadog";
import { isLocalEnvironment } from "@/utils/environment.util";

const isDatadogEnabled = !isLocalEnvironment();

/**
 * Captures and reports an exception using Datadog.
 *
 * This function should only be used in scenarios where the `useAppMonitor` hook cannot be utilized.
 * The preferred approach for exception monitoring is to use the `useAppMonitor` hook.
 *
 * @param error - The error or exception to be captured and reported.
 * @param {Object} options - The options for the capture exception.
 * @param {Object} options.context - The context for the capture exception.
 * @param {ErrorSource} options.errorSource - The error source for the capture exception for Datadog.
 */
const captureException = (
  error: unknown,
  {
    context,
    errorSource = ErrorSource.CUSTOM,
  }: {
    context?: Record<string, unknown>;
    errorSource?: ErrorSource;
  } = {
    context: {},
    errorSource: ErrorSource.CUSTOM,
  },
  // eslint-disable-next-line max-params -- error is required, anything else is optional
) => {
  if (!isDatadogEnabled) return;

  // Datadog RUM error tracking
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorStack =
    error instanceof Error && error.stack ? error.stack : "empty stack";
  void DdRum.addError(errorMessage, errorSource, errorStack, context);
};

/**
 * Captures and reports a message using Datadog.
 *
 * This function should only be used in scenarios where the `useAppMonitor` hook cannot be utilized.
 * The preferred approach for exception monitoring is to use the `useAppMonitor` hook.
 *
 * @param message - The message to be captured and reported.
 * @param {Object} options - The options for the capture message.
 * @param {Object} options.context - The context for the capture message.
 * @param {ErrorSource} options.errorSource - The error source for the capture message for Datadog.
 */
const captureMessage = (
  message: string,
  {
    context,
    errorSource = ErrorSource.CUSTOM,
  }: {
    context?: Record<string, unknown>;
    errorSource?: ErrorSource;
  } = {
    context: {},
    errorSource: ErrorSource.CUSTOM,
  },
  // eslint-disable-next-line max-params -- message is required, anything else is optional
) => {
  if (!isDatadogEnabled) return;

  // Datadog RUM error tracking
  void DdRum.addError(message, errorSource, "empty stack", context);
};

type LogParams = { context?: Record<string, unknown>; message: string };

/** Logs an info message to Datadog Logs. */
const logInfo = ({ context, message }: LogParams) => {
  if (!isDatadogEnabled) return;
  void DdLogs.info(message, context);
};

const useAppMonitor = () => {
  const identifyUser = useCallback((userId: string) => {
    if (!isDatadogEnabled) return;

    void setDatadogUser(userId);
  }, []);

  return {
    captureException,
    captureMessage,
    identifyUser,
    logInfo,
  };
};

const initAppMonitor = (onComplete?: () => void) => {
  if (!isDatadogEnabled) {
    onComplete?.();
    return;
  }

  // Datadog RUM view tracking: initialize in all non-local environments. The platform facade owns
  // how init is done (native enables Session Replay; web inits the browser SDKs).
  void initDatadog().then(() => onComplete?.());
};

type UseAppMonitor = ReturnType<typeof useAppMonitor>;

export type { UseAppMonitor };
export { captureException, captureMessage, initAppMonitor, logInfo };

export default useAppMonitor;
